import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api, { formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/EmptyState";
import { Tag, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["", "Farmhouses", "Resorts", "Banquet Halls", "Function Halls"];
const empty = { code: "", discount_type: "percent", discount_value: "", min_booking_amount: "", max_discount: "", usage_limit: "", per_customer_limit: "", category: "", location: "", active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.code || !f.discount_value) return toast.error("Enter a code and discount value.");
    try {
      await api.post("/admin/coupons", {
        ...f, discount_value: Number(f.discount_value), min_booking_amount: Number(f.min_booking_amount) || 0,
        max_discount: Number(f.max_discount) || 0, usage_limit: Number(f.usage_limit) || 0, per_customer_limit: Number(f.per_customer_limit) || 0,
      });
      toast.success("Coupon created");
      setF(empty);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (c) => { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); load(); };
  const del = async (c) => { await api.delete(`/admin/coupons/${c.id}`); load(); };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Coupons</h1>
      <p className="mt-1 text-sm text-stone-500">Create promotional codes. (Discounts apply once payments are live — this is the coupon foundation.)</p>

      <div className="mt-5 rounded-2xl border border-stone-200 bg-white p-6">
        <p className="mb-3 font-heading font-semibold">Create coupon</p>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div><Label className="text-xs text-stone-500">Code</Label><Input data-testid="coupon-code" value={f.code} onChange={set("code")} className="mt-1 rounded-xl" placeholder="WELCOME10" /></div>
          <div>
            <Label className="text-xs text-stone-500">Type</Label>
            <Select value={f.discount_type} onValueChange={set("discount_type")}>
              <SelectTrigger data-testid="coupon-type" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-stone-500">Value</Label><Input data-testid="coupon-value" type="number" value={f.discount_value} onChange={set("discount_value")} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs text-stone-500">Min booking ₹</Label><Input type="number" value={f.min_booking_amount} onChange={set("min_booking_amount")} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs text-stone-500">Max discount ₹</Label><Input type="number" value={f.max_discount} onChange={set("max_discount")} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs text-stone-500">Usage limit</Label><Input type="number" value={f.usage_limit} onChange={set("usage_limit")} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs text-stone-500">Per customer</Label><Input type="number" value={f.per_customer_limit} onChange={set("per_customer_limit")} className="mt-1 rounded-xl" /></div>
          <div><Label className="text-xs text-stone-500">Location (optional)</Label><Input data-testid="coupon-location" value={f.location} onChange={set("location")} className="mt-1 rounded-xl" placeholder="Moinabad" /></div>
          <div>
            <Label className="text-xs text-stone-500">Category</Label>
            <Select value={f.category || "all"} onValueChange={(v) => set("category")(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="coupon-category" className="mt-1 rounded-xl"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c || "all"} value={c || "all"}>{c || "All categories"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="coupon-create" onClick={create} className="mt-4 rounded-xl bg-primary hover:bg-green-900">Create Coupon</Button>
      </div>

      <div className="mt-6 space-y-2">
        {coupons.length === 0 ? (
          <EmptyState icon={Tag} title="No coupons yet" message="Create your first promotional code above." />
        ) : coupons.map((c) => (
          <div key={c.id} data-testid={`coupon-${c.id}`} className="flex items-center justify-between rounded-2xl border border-stone-200 bg-white p-4">
            <div>
              <p className="font-heading font-bold text-primary">{c.code}</p>
              <p className="text-sm text-stone-500">{c.discount_type === "percent" ? `${c.discount_value}% off` : `₹${c.discount_value} off`}{c.min_booking_amount ? ` · min ₹${c.min_booking_amount}` : ""}{c.category ? ` · ${c.category}` : ""}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2"><span className="text-xs text-stone-500">Active</span><Switch data-testid={`coupon-toggle-${c.id}`} checked={c.active} onCheckedChange={() => toggle(c)} /></div>
              <button data-testid={`coupon-del-${c.id}`} onClick={() => del(c)} className="text-stone-400 hover:text-destructive"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
